import { useState, useEffect, useCallback } from 'react';
import { ApiError, asList } from '../lib/api';
import { hrApi, type Employee } from '../lib/hrApi';
import { attendanceApi, type StaffAttendance } from '../lib/payrollApi';
import { DataTable, type Column } from '../components/DataTable';

const STATUSES = ['Present', 'Absent', 'HalfDay', 'Holiday'];

/// The staff attendance register (tenant-admin). Staff can check themselves in; an admin can mark or
/// OVERRIDE any day — and the record keeps the original source, so an override is visible.
/// A day covered by approved leave is always shown as OnLeave and can't be marked absent.
export function StaffAttendancePage() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [records, setRecords] = useState<StaffAttendance[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    hrApi.listEmployees().then((r) => setEmployees(asList(r))).catch(() => setEmployees([]));
    attendanceApi.register(date).then((r) => setRecords(asList(r))).catch(() => setRecords([]));
  }, [date]);
  useEffect(load, [load]);

  const recordFor = (id: string) => records.find((r) => r.employeeId === id) ?? null;

  /**
   * Columns.
   *
   * ⚠ Inside the component because every cell reads `records` — the marks change as the clerk works,
   * and a module-scope definition would render stale rows.
   */
  const columns: Column<Employee>[] = [
    {
      id: 'employee', header: 'Employee', sortable: true, primary: true,
      // Code included in the sort value so typing it finds the row — a payroll clerk holds the code,
      // not the spelling of a name.
      value: (e) => `${e.fullName} ${e.employeeCode}`,
      cell: (e) => (
        <>
          <strong>{e.fullName}</strong>{' '}
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{e.employeeCode}</span>
        </>
      ),
    },
    {
      id: 'status', header: 'Status', sortable: true,
      // ⚠ Unmarked sorts to the TOP, because those are the rows the clerk still has to act on.
      // Sorting them last would bury the only ones that need attention.
      value: (e) => recordFor(e.id)?.status ?? '',
      cell: (e) => {
        const r = recordFor(e.id);
        return r
          ? <span className={`pill ${r.status === 'Present' ? 'pill-ok' : r.status === 'Absent' ? 'pill-danger' : 'pill-muted'}`} style={{ fontSize: 11 }}>{r.status}</span>
          : <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>not marked</span>;
      },
    },
    {
      id: 'source', header: 'Source', sortable: true, hideMd: true,
      value: (e) => recordFor(e.id)?.source ?? '',
      cell: (e) => {
        const r = recordFor(e.id);
        if (!r) return '—';
        return (
          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            {r.source === 'SelfCheckIn' ? 'self' : r.source === 'LeaveDerived' ? 'leave' : 'admin'}
            {r.overriddenFrom && (
              <span title={`overridden from ${r.overriddenFrom}`} style={{ color: 'var(--warn,#B7791F)' }}> · overridden</span>
            )}
          </span>
        );
      },
    },
    {
      id: 'inout', header: 'In / Out', hideSm: true,
      value: (e) => recordFor(e.id)?.checkInAt ?? '',
      cell: (e) => {
        const r = recordFor(e.id);
        const time = (v?: string | null) =>
          v ? new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
        return (
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {time(r?.checkInAt)} / {time(r?.checkOutAt)}
          </span>
        );
      },
    },
    {
      id: 'mark', header: 'Mark', actions: true,
      cell: (e) => {
        const r = recordFor(e.id);
        if (r?.status === 'OnLeave')
          return <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>on approved leave</span>;
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            {STATUSES.map((st) => (
              <button key={st} className="btn" style={{ padding: '2px 8px', fontSize: 11 }}
                onClick={() => mark(e.id, st)}>
                {st === 'HalfDay' ? 'Half' : st}
              </button>
            ))}
          </div>
        );
      },
    },
  ];

  const mark = async (employeeId: string, status: string) => {
    setError(null);
    try { await attendanceApi.mark({ employeeId, date, status }); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not mark.'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>People</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Staff attendance</h1>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 14 }}>
        <div><label className="label">Date</label><input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 170 }} /></div>
      </div>

      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

      <DataTable
        rows={employees}
        columns={columns}
        rowKey={(e) => e.id}

        search
        searchPlaceholder="Search staff by name or code…"

        // ⚠ NO PAGINATION on this one, deliberately.
        //
        // This is a MARKING screen, not a report. A clerk works down the whole staff list in one
        // pass, and paging it means finishing page one, clicking next, and losing their place —
        // then wondering later who was missed. Search is the right tool for finding one person;
        // pagination is the wrong tool for marking everyone.
        pageSize={500}

        empty={{ title: 'No employees', body: 'Add staff before marking attendance.' }}
      />

      <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
        Marking a day overrides whatever was there — including a staff member's own check-in — and the register records that an
        admin changed it. Approved-leave days can't be marked absent.
      </p>
    </div>
  );
}

